<template>
  <main class="main inventory-page">
    <div class="container">
      <UiBreadCrumbsV2
        :items="[
          { title: t('inventory.title'), link: localePath('/inventory') },
          {
            title: t('inventory.user_nickname_inventory', { nickname }),
            link: '',
          },
        ]"
      />
      <div
        v-if="data?.errors?.length"
        class="mb24"
      >
        <UiAlert
          :description="t('inventory.not_found_description')"
          :title="t('inventory.not_found')"
          icon="info"
          type="error"
        />
      </div>
      <PagesInventorySteamIdChecker inner />
      <div
        v-if="data?.data?.userInventoryProfile"
        class="main-wrapper"
      >
        <div class="inventory-page_sidebar">
          <PagesInventoryInnerMainInfo
            :catalog="catalog?.total"
            :data="data?.data?.userInventoryProfile"
          />

          <PagesInventoryInnerAlsoLike
            :market="catalog?.market?.market_name"
            :steam64="steam64"
          />
        </div>
        <div class="inventory-page_wrap">
          <PagesInventoryInnerMainCatalog
            ref="catalog"
            :steam64="steam64"
          />
          <LazyPagesInventoryInnerChartWrapper :market="catalog?.market?.market_name" />
        </div>
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import { useStore } from '~/store';
import { useModalStore } from '~/store/modal';
import { ModalIds } from '~/types/Modals';

const localePath = useLocalePath();

const route = useRoute();
const store = useStore();
const modalStore = useModalStore();
const { locale, t } = useI18n();

const steam64 = ref(route.params.id as string);
const catalog = ref<any>(null);
const market = computed(() => catalog?.value?.market?.market_name || 'steamcommunity');

const { data, refresh } = await useAsyncData(`userProfile${route.params.id}`, () =>
  getUserInventoryProfile(market.value, steam64.value));

const nickname = computed(
  () =>
    data?.value?.data?.userInventoryProfile?.persona_name
    || data?.value?.data?.userInventoryProfile?.steam64
    || '',
);

watch([() => market.value], () => {
  refresh();
});

onMounted(() => {
  const stState = data?.value?.data?.userInventoryProfile?.steam_state;
  const ownInventory = data?.value?.data?.userInventoryProfile?.steam64 === store.user?.steamid64;
  if (stState && ['private', 'private_inventory'].includes(stState) && ownInventory) {
    setTimeout(() => {
      modalStore.openModal(ModalIds.INVENTORY_PRIVATE);
    }, 500);
  }
});

const title = computed(() => {
  return t('inventory.seo.inner_page.title', { username: nickname.value });
});
const description = computed(() => {
  return t('inventory.seo.inner_page.desc', { username: nickname.value });
});
const weaponIcon = 'https://csmarketcap.com/img/logo-bg-black.png';
const { alternateLinks, canonical } = useAlternateLinks(`/inventory`);

useHead({
  title: () => title.value,
  meta: [
    {
      name: 'keywords',
      content: 'steam, counter strike global offensive, csgo, cs2, skins, items, inventory',
    },
  ],
  link: () => alternateLinks.value,
});

useSeoMeta({
  title: () => title.value,
  ogTitle: () => title.value,
  description: () => description.value,
  ogDescription: () => description.value,
  ogImage: () => weaponIcon,
  twitterCard: 'summary_large_image',
  twitterTitle: () => title.value,
  twitterDescription: () => description.value,
  twitterImage: () => weaponIcon,
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value,
});

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      { name: t('main'), item: '/' },
      {
        name: t('inventory.title'),
        item: localePath('/inventory'),
      },
      {
        name: title,
        item: localePath(`/inventory/${route.params.id}`),
      },
    ],
  }),
]);
</script>

<style lang="scss" scoped>
@use "sass:color";

.inventory-page {
  padding: 18rem 0 4rem;
  font-size: 16px;
  color: var(--surfaces-on-surface-variable);

  section {
    margin-bottom: 40px;
  }

  .main-wrapper {
    display: flex;
    gap: 32px;
    justify-content: space-between;
    align-items: flex-start;
  }

  &_wrap {
    flex-grow: 1;
    display: grid;
    gap: 40px;
  }

  &_sidebar {
    // box-sizing: border-box;
    width: 400px;
    min-width: 400px;
    display: flex;
    flex-direction: column;
    gap: 40px;
  }
}

@media screen and (max-width: 1115px) {
  .inventory-page {
    padding-top: 6rem;
  }
}

@media screen and (max-width: 989px) {
  .inventory-page {
    section {
      margin-bottom: 24px;
    }

    .main-wrapper {
      flex-direction: column;
      gap: 24px;
    }

    .inventory-page_sidebar,
    .inventory-page_wrap {
      width: 100%;
      max-width: 100%;
      min-width: 100%;
      display: contents;
    }

    .alsolike {
      order: 1;
      width: 100%;
    }

    .inventory-info,
    .inventory-list,
    .skin-chart {
      width: 100%;
      margin-bottom: 0;
    }
  }
}
</style>
